import { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { spacing, colors } from 'ui/theme';
import { Popover, PopoverContent, PopoverTrigger, PopoverPlacements } from 'ui/popover';
import { InAppTooltipBreakdown } from 'modules/campaignAnalytics/components/InAppTooltipBreakdown';
import { GREEN_SHADES } from 'modules/campaignAnalytics/constants';
import { pxToRem } from 'utils/convert';
import { kebabCase } from 'lodash';
import { ComparisonBarTooltip } from './ComparisonBarTooltip';
import { InAppDestination } from '../types';
import { colorsByMedium, hoverColorsByMedium } from '../constants';

type GraphBarWithBreakdownProps = {
  $width: number;
  destinations?: InAppDestination[];
  numberType: string;
  medium: string;
  numberOfCampaigns: number;
  metricName: string;
};

type GraphBarContainerProps = {
  $width: number;
};
type GraphBarProps = {
  $width: number;
  $color: string;
};

type PercentageGraphBarProps = {
  $width: number;
  $color: string;
  $hoverColor: string;
};

const GraphBarContainer = styled.div<GraphBarContainerProps>`
  width: ${(props) => props.$width}%;
  height: ${spacing.base};
  margin-bottom: ${spacing.half};
  display: flex;
  justify-content: flex-start;
`;

const GraphBar = styled.div<GraphBarProps>`
  background-color: ${(props) => props.$color};
  width: ${(props) => pxToRem(props.$width)};
  height: ${spacing.base};
`;

const PercentageGraphBar = styled.div<PercentageGraphBarProps>`
  background-color: ${(props) => props.$color};
  width: ${(props) => pxToRem(props.$width)};
  height: ${spacing.base};
  &:hover {
    background: ${(props) => props.$hoverColor};
  }
`;

const Affordance = styled.span`
  height: ${spacing.base};
  width: ${pxToRem(10)};
`;

const StyledPopoverContent = styled(PopoverContent)`
  max-width: ${pxToRem(400)};
  background: ${colors.grey80};
  svg {
    fill: ${colors.grey80};
  }
`;

export const GraphBarWithBreakdown = ({
  $width,
  destinations,
  numberType,
  medium,
  numberOfCampaigns,
  metricName,
}: GraphBarWithBreakdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(0);

  useEffect(() => {
    if (containerRef?.current) {
      setBarWidth(containerRef?.current?.getBoundingClientRect()?.width || 0);
    }
  }, [containerRef.current]);

  let total = 0;
  destinations?.forEach((dest) => {
    total += dest?.value ? parseInt(dest?.value, 10) : 0;
  });

  if (numberType === 'percentage') {
    return (
      <GraphBarContainer
        data-test={`graph-bar-breakdown-${kebabCase(metricName)}`}
        $width={$width}
        ref={containerRef}
      >
        <Popover>
          <StyledPopoverContent side={PopoverPlacements.Top}>
            <ComparisonBarTooltip
              medium={medium}
              metricName={metricName}
              numberOfCampaigns={numberOfCampaigns}
              value={
                <>
                  {destinations?.map(({ appName, platform, value }, idx) => (
                    <InAppTooltipBreakdown
                      key={`${appName}-${idx}`}
                      destination={{
                        value: parseFloat(value),
                        appName,
                        platform,
                      }}
                      total={total}
                      numberType={numberType}
                    />
                  ))}
                </>
              }
            />
          </StyledPopoverContent>
          <PopoverTrigger>
            <PercentageGraphBar
              $color={colorsByMedium[medium]}
              // @ts-ignore
              $width={barWidth}
              $hoverColor={hoverColorsByMedium[medium]}
            />
            {$width < 10 && <Affordance />}
          </PopoverTrigger>
        </Popover>
      </GraphBarContainer>
    );
  }

  return (
    <GraphBarContainer
      data-test={`graph-bar-in-app-breakdown-${kebabCase(metricName)}`}
      $width={$width}
      ref={containerRef}
    >
      {destinations?.map(({ appName, platform, value }, idx) => {
        const percentage = parseInt(value, 10) / total;
        return (
          <Popover key={`${appName}-${idx}`}>
            <StyledPopoverContent side={PopoverPlacements.Top}>
              <ComparisonBarTooltip
                medium={medium}
                metricName={metricName}
                numberOfCampaigns={numberOfCampaigns}
                value={
                  <InAppTooltipBreakdown
                    destination={{
                      value: parseInt(value, 10),
                      appName,
                      platform,
                    }}
                    total={total}
                    numberType={numberType}
                  />
                }
              />
            </StyledPopoverContent>
            <PopoverTrigger>
              <GraphBar
                $color={GREEN_SHADES[idx % GREEN_SHADES.length]}
                // @ts-ignore
                $width={percentage * barWidth}
              />
            </PopoverTrigger>
          </Popover>
        );
      })}
    </GraphBarContainer>
  );
};
